import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NewsletterSettings {
  id: string;
  enabled: boolean;
  badge_text: string;
  title: string;
  description: string;
  email_placeholder: string;
  button_text: string;
  fine_print: string;
  mode: 'internal' | 'external';
  external_html: string;
  provider: string;
  provider_api_key: string | null;
  provider_list_id: string | null;
  double_opt_in: boolean;
  portal_id: string;
  api_url: string;
  updated_at: string;
}

const DEFAULTS: Omit<NewsletterSettings, 'id' | 'updated_at'> = {
  enabled: true,
  badge_text: 'Tedenske BUMnovice',
  title: 'Ne zamudite najbolj branih zgodb tedna',
  description: 'Prejemajte izbrane novice, trače, zanimivosti in ekskluzivne vsebine neposredno v vaš e-poštni nabiralnik.',
  email_placeholder: 'Vnesite svoj e-poštni naslov',
  button_text: 'Naroči se na novice',
  fine_print: 'Odjava kadarkoli. Vaše podatke varujemo.',
  mode: 'internal',
  external_html: '',
  provider: 'none',
  provider_api_key: null,
  provider_list_id: null,
  double_opt_in: false,
  portal_id: 'bum-novice',
  api_url: 'https://zoqxgkkqqrgppxrysfsv.supabase.co/functions/v1',
};

export function useNewsletterSettings() {
  return useQuery({
    queryKey: ['newsletter-settings'],
    queryFn: async (): Promise<NewsletterSettings> => {
      const { data, error } = await (supabase as any)
        .from('newsletter_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching newsletter settings:', error);
        return { id: '', updated_at: '', ...DEFAULTS } as NewsletterSettings;
      }

      if (!data) {
        return { id: '', updated_at: '', ...DEFAULTS } as NewsletterSettings;
      }

      return data as NewsletterSettings;
    },
    staleTime: 30 * 1000,
  });
}

export function useUpdateNewsletterSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<NewsletterSettings> & { id: string }) => {
      const { id, ...updates } = settings;
      const { error } = await (supabase as any)
        .from('newsletter_settings')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-settings'] });
    },
  });
}
